import { useState } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { Loader2, CheckCircle, Sparkles } from 'lucide-react';

export default function LeadForm({ openModal }) {
  const [formData, setFormData] = useState({
    name: '',
    whatsapp: '',
    email: '',
    level: 'beginner'
  });
  const [agreed, setAgreed] = useState(false);
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [errorMsg, setErrorMsg] = useState('');
  const [submittedName, setSubmittedName] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMsg('');

    try {
      await addDoc(collection(db, 'registrations'), {
        ...formData,
        createdAt: serverTimestamp()
      });
      setSubmittedName(formData.name);
      setStatus('success');
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        'event': 'generate_lead',
        'form_name': 'registration_form',
        'platform': 'Lobab School'
      });
      setFormData({ name: '', whatsapp: '', email: '', level: 'beginner' });
    } catch (error) {
      console.error("Error adding document: ", error);
      setStatus('error');
      setErrorMsg('حدث خطأ أثناء إرسال البيانات. يرجى المحاولة مرة أخرى أو التأكد من إعدادات Firebase.');
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  return (
    <section id="register" className="py-32 bg-slate-100 dark:bg-zinc-950 relative overflow-hidden flex flex-col items-center transition-colors duration-500">
      {/* Abstract Grid and Glows */}
      <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-5 dark:opacity-10 mix-blend-screen pointer-events-none transition-opacity duration-500"></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-primary-600 rounded-full w-[600px] h-[600px] blur-[100px] dark:blur-[200px] opacity-[0.05] dark:opacity-10 transform -translate-y-1/4 pointer-events-none transition-all duration-500"></div>

      <div className="container mx-auto px-4 relative z-10 flex flex-col items-center w-full">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center justify-center gap-2 py-1.5 px-5 rounded-full bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 text-primary-600 dark:text-primary-400 text-lg font-bold mb-8 backdrop-blur-md shadow-sm dark:shadow-[0_0_15px_rgba(224,0,0,0.1)] transition-all duration-500">
            <Sparkles className="w-4 h-4" />
            <span>مقاعد محدودة</span>
          </div>
          <h3 className="text-4xl md:text-6xl font-extrabold mb-6 text-slate-900 dark:text-white drop-shadow-sm dark:drop-shadow-md transition-colors duration-500">
            هل أنت مستعد <span className="text-transparent bg-clip-text bg-gradient-to-l from-primary-600 to-primary-700 dark:from-primary-400 dark:to-primary-600">لبدء التحدي؟</span>
          </h3>
          <p className="text-xl text-slate-600 dark:text-zinc-400 font-light leading-relaxed max-w-2xl mx-auto transition-colors duration-500">
            سجل بياناتك الآن وسنتواصل معك بكافة تفاصيل الدورة (المواعيد، الرسوم، وآلية الانضمام).
          </p>
        </div>

        <div className="w-full max-w-xl bg-white/70 dark:bg-zinc-900/40 backdrop-blur-2xl rounded-lg p-10 md:p-12 shadow-xl dark:shadow-[0_20px_50px_rgba(0,0,0,0.5)] relative border border-slate-200 dark:border-white/10 group hover:border-[#e00000]/20 dark:hover:border-[#e00000]/30 transition-colors duration-500">

          {/* Subtle form top highlight */}
          <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-slate-300 dark:via-white/20 to-transparent transition-colors duration-500"></div>

          <form onSubmit={handleSubmit} className="space-y-8">
            <div>
              <label htmlFor="name" className="block text-lg font-bold text-slate-700 dark:text-zinc-300 mb-3 ml-1 transition-colors duration-500">الاسم الثلاثي</label>
              <input
                type="text"
                id="name"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                className="w-full px-5 py-4 rounded-2xl bg-white dark:bg-black/50 border border-slate-200 dark:border-white/10 focus:border-primary-500 focus:ring-4 focus:ring-primary-500/20 outline-none transition-all text-slate-900 dark:text-white font-medium placeholder:text-slate-400 dark:placeholder:text-zinc-600 shadow-sm dark:shadow-inner"
                placeholder="محمد أحمد عبدالله"
              />
            </div>

            <div>
              <label htmlFor="whatsapp" className="block text-lg font-bold text-slate-700 dark:text-zinc-300 mb-3 ml-1 transition-colors duration-500">رقم الواتساب</label>
              <div className="flex shadow-sm dark:shadow-inner rounded-2xl focus-within:ring-4 focus-within:ring-primary-500/20 focus-within:border-primary-500 transition-all border border-slate-200 dark:border-white/10 overflow-hidden bg-white dark:bg-black/50" dir="ltr">
                <span className="inline-flex items-center px-5 border-r border-slate-200 dark:border-white/5 bg-slate-50 dark:bg-zinc-900/50 text-slate-500 dark:text-zinc-500 font-bold transition-colors duration-500">
                  +
                </span>
                <input
                  type="tel"
                  id="whatsapp"
                  name="whatsapp"
                  required
                  value={formData.whatsapp}
                  onChange={handleChange}
                  className="w-full px-5 py-4 bg-transparent border-none focus:ring-0 outline-none text-slate-900 dark:text-white font-medium font-sans placeholder:text-slate-600 dark:placeholder:text-zinc-600 transition-colors duration-500"
                  placeholder="967770000000"
                />
              </div>
              <p className="text-md text-slate-500 dark:text-zinc-500 mt-3 font-medium mr-1 transition-colors duration-500">الرجاء إدخال الرقم مع رمز الدولة</p>
            </div>

            <div>
              <label htmlFor="email" className="block text-lg font-bold text-slate-700 dark:text-zinc-300 mb-3 ml-1 transition-colors duration-500">البريد الإلكتروني</label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="w-full px-5 py-4 rounded-2xl bg-white dark:bg-black/50 border border-slate-200 dark:border-white/10 focus:border-primary-500 focus:ring-4 focus:ring-primary-500/20 outline-none transition-all text-slate-900 dark:text-white font-medium placeholder:text-slate-400 dark:placeholder:text-zinc-600 shadow-sm dark:shadow-inner"
                placeholder="example@gmail.com"
                dir="ltr"
                style={{ textAlign: 'left' }}
              />
            </div>

            <div>
              <label htmlFor="level" className="block text-lg font-bold text-slate-700 dark:text-zinc-300 mb-3 ml-1 transition-colors duration-500">مستواك الحالي في التصميم</label>
              <select
                id="level"
                name="level"
                value={formData.level}
                onChange={handleChange}
                className="w-full px-5 py-4 rounded-2xl bg-white dark:bg-black/50 border border-slate-200 dark:border-white/10 focus:border-primary-500 focus:ring-4 focus:ring-primary-500/20 outline-none transition-all text-slate-900 dark:text-white font-medium appearance-none shadow-sm dark:shadow-inner"
                style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='gray'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundPosition: 'left 1rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.5em 1.5em' }}
              >
                <option value="beginner" className="bg-white dark:bg-zinc-900 text-slate-900 dark:text-white">مبتدئ (أعرف الأساسيات فقط)</option>
                <option value="intermediate" className="bg-white dark:bg-zinc-900 text-slate-900 dark:text-white">متوسط (صممت بعض الشعارات/الهويات سابقاً)</option>
              </select>
            </div>

            {status === 'error' && (
              <div className="bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 p-5 rounded-2xl text-lg font-bold border border-red-200 dark:border-red-500/20 flex items-start gap-3 backdrop-blur-sm transition-colors duration-500">
                <svg className="w-5 h-5 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                <span className="leading-relaxed">{errorMsg}</span>
              </div>
            )}

            <div>
              <label className="flex items-center gap-3 cursor-pointer group w-fit">
                <div className="relative flex items-center justify-center w-6 h-6 rounded-md border border-slate-300 dark:border-white/20 bg-slate-50 dark:bg-black/50 group-hover:border-primary-500 transition-colors shadow-sm dark:shadow-inner shrink-0">
                  <input
                    type="checkbox"
                    className="absolute opacity-0 cursor-pointer w-full h-full"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    required
                  />
                  {agreed && <CheckCircle className="w-4 h-4 text-primary-600 dark:text-primary-500" />}
                </div>
                <span className="text-lg font-medium text-slate-700 dark:text-zinc-300 select-none leading-relaxed transition-colors duration-500">
                  أوافق على{' '}
                  <span onClick={(e) => { e.preventDefault(); openModal('terms'); }} className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors cursor-pointer border-b border-primary-500/20 hover:border-primary-500 pb-0.5">الشروط والأحكام</span>
                  {' '}و{' '}
                  <span onClick={(e) => { e.preventDefault(); openModal('privacy'); }} className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors cursor-pointer border-b border-primary-500/20 hover:border-primary-500 pb-0.5">سياسة الخصوصية</span>
                </span>
              </label>
            </div>

            <button
              type="submit"
              disabled={status === 'loading' || !agreed}
              className="w-full bg-primary-600 hover:bg-primary-500 text-white font-extrabold text-lg py-5 rounded-2xl shadow-[0_0_15px_rgba(224,0,0,0.2)] dark:shadow-[0_0_20px_rgba(224,0,0,0.3)] hover:shadow-[0_0_30px_rgba(224,0,0,0.5)] transition-all duration-300 flex justify-center items-center gap-3 group disabled:opacity-70 disabled:cursor-not-allowed hover:-translate-y-1 ring-1 ring-primary-500/10 dark:ring-white/10"
            >
              {status === 'loading' ? (
                <Loader2 className="w-6 h-6 animate-spin" />
              ) : (
                <>
                  <span>أرسل طلب الانضمام</span>
                </>
              )}
            </button>
          </form>
        </div>
      </div>

      {/* Success Modal */}
      {status === 'success' && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 dark:bg-black/60 backdrop-blur-md animate-in fade-in duration-300">
          <div className="bg-white dark:bg-zinc-900 rounded-lg w-full max-w-xl overflow-hidden shadow-2xl relative animate-in zoom-in-95 duration-500 p-8 md:p-12 text-center flex flex-col items-center border border-slate-200 dark:border-white/10">

            {/* Close Button */}
            <button
              onClick={() => setStatus('idle')}
              className="absolute top-6 left-6 text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors bg-slate-100 dark:bg-white/5 hover:bg-slate-200 dark:hover:bg-white/10 rounded-full p-2"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>

            <div className="w-28 h-28 bg-green-50 dark:bg-green-500/10 rounded-full flex items-center justify-center mb-8 border border-green-200 dark:border-green-500/20 shadow-sm dark:shadow-[0_0_30px_rgba(34,197,94,0.2)]">
              <CheckCircle className="w-14 h-14 text-green-500" />
            </div>

            <h4 className="text-3xl md:text-4xl font-extrabold text-slate-900 dark:text-white mb-4">تم استلام طلبك بنجاح!</h4>

            <p className="text-slate-600 dark:text-zinc-400 text-lg leading-relaxed mb-10 max-w-md">
              لضمان حجز مقعدك وتأكيد هويتك، يرجى الضغط على الزر أدناه لإتمام التسجيل عبر الواتساب الخاص بالمنصة.
            </p>

            <a
              href={`https://wa.me/967783338339?text=${encodeURIComponent(`أهلاً لباب سكول، أنا الطالب ${submittedName}، لقد قمت بالتسجيل في الدورة وأريد تأكيد انضمامي!`)}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setStatus('idle')}
              className="w-full bg-[#25D366] hover:bg-[#20bd5a] text-white font-extrabold text-2xl py-6 rounded-2xl shadow-[0_0_20px_rgba(37,211,102,0.3)] hover:shadow-[0_0_30px_rgba(37,211,102,0.5)] transition-all duration-300 flex justify-center items-center gap-4 group hover:-translate-y-1"
            >
              <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
              <span>إتمام التسجيل عبر واتساب</span>
            </a>

            <p className="text-lg font-bold text-slate-500 mt-6 md:mt-8 opacity-80 border-b border-dashed border-slate-300 dark:border-zinc-700 pb-1">
              سيتم الرد عليك خلال أقل من ساعة ⏳
            </p>

          </div>
        </div>
      )}
    </section>
  );
}
